import React from "react";
import { useParams, Link } from "react-router-dom";
import { useCompany } from "@/context/CompanyContext";
import { trackEvent } from "@/lib/ga";
import { SITE_URL } from "@/lib/api";
import { orgGraph, providerRef, webPage } from "@/lib/schema";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import FaqAccordion from "@/components/FaqAccordion";
import LeadForm from "@/components/LeadForm";
import NotFound from "@/pages/NotFound";
import { WARRANTY_SPOKES, getSpoke } from "@/data/warrantyCluster";
import { ShieldCheck, Phone, ArrowRight, CheckCircle2, Info } from "lucide-react";

export default function LifetimeWarrantySpoke() {
  const { spokeSlug } = useParams();
  const { company } = useCompany();
  const spoke = getSpoke(spokeSlug);
  if (!spoke) return <NotFound />;

  const path = `/lifetime-warranty/${spoke.slug}/`;
  const otherSpokes = WARRANTY_SPOKES.filter((s) => s.slug !== spoke.slug);

  const jsonLd = [
    ...orgGraph(company),
    webPage(path, spoke.seo.title, spoke.seo.description),
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Lifetime Warranty", item: `${SITE_URL}/lifetime-warranty/` },
        { "@type": "ListItem", position: 3, name: spoke.navLabel, item: `${SITE_URL}${path}` },
      ],
    },
    {
      "@context": "https://schema.org", "@type": "Service",
      name: spoke.h1, serviceType: "HVAC Lifetime Warranty Protection",
      description: spoke.seo.description, provider: providerRef(),
      areaServed: company.primary_service_area, url: `${SITE_URL}${path}`,
      isPartOf: { "@id": `${SITE_URL}/lifetime-warranty/#webpage` },
    },
    {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: spoke.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
  ];

  return (
    <>
      <Seo title={spoke.seo.title} description={spoke.seo.description} path={path} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative bg-navy text-white overflow-hidden" data-testid="warranty-spoke-hero">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-14 md:pb-20">
          <div className="[&_*]:!text-slate-300">
            <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Lifetime Warranty", to: "/lifetime-warranty" }, { label: spoke.navLabel }]} />
          </div>
          <p className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-launch">
            <ShieldCheck className="h-4 w-4" /> {spoke.eyebrow}
          </p>
          <h1 className="mt-3 max-w-3xl text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08]">{spoke.h1}</h1>
          <p className="mt-4 max-w-2xl text-xl sm:text-2xl font-heading font-semibold text-white">{spoke.subhead}</p>
          <p className="mt-4 max-w-2xl text-lg text-slate-200 leading-relaxed">{spoke.intro}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link to="/contact-us" onClick={() => trackEvent("warranty_cta_click", { location: `spoke_${spoke.slug}`, action: "consult" })} data-testid="spoke-hero-cta" className="inline-flex items-center justify-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3.5 px-8 rounded-md shadow-lg transition-colors">
              Ask About Your Warranty <ArrowRight className="h-5 w-5" />
            </Link>
            <a href={`tel:${company.phone_href || ""}`} onClick={() => trackEvent("phone_click", { location: `warranty_spoke_${spoke.slug}` })} data-testid="spoke-hero-call" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold py-3.5 px-8 rounded-md transition-colors">
              <Phone className="h-5 w-5" /> Call {company.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-10">
            {spoke.sections.map((sec, i) => (
              <div key={sec.h2} data-testid={`spoke-section-${i}`}>
                <h2 className="text-2xl sm:text-3xl font-bold text-navy tracking-tight">{sec.h2}</h2>
                {(sec.paragraphs || []).map((p, j) => (
                  <p key={j} className="mt-4 text-base text-slate-700 leading-relaxed">{p}</p>
                ))}
                {sec.bullets && (
                  <ul className="mt-5 space-y-3">
                    {sec.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-cool mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700 leading-relaxed">{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            {/* Back to pillar + sibling spokes */}
            <div className="bg-navy rounded-xl p-6 sm:p-8 text-white dot-grid" data-testid="spoke-cluster-nav">
              <h3 className="text-xl font-bold">Explore the Lifetime Warranty</h3>
              <p className="mt-2 text-sm !text-slate-200">Everything you need to know about protecting your new HVAC investment.</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/lifetime-warranty" data-testid="spoke-to-pillar" className="inline-flex items-center gap-1.5 bg-launch hover:bg-launch-dark rounded-full px-4 py-2 text-sm font-semibold text-white transition-colors">
                  Lifetime Warranty Overview <ArrowRight className="h-4 w-4" />
                </Link>
                {otherSpokes.map((s) => (
                  <Link key={s.slug} to={`/lifetime-warranty/${s.slug}`} data-testid={`spoke-link-${s.slug}`} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full px-4 py-2 text-sm font-medium text-white transition-colors">
                    {s.navLabel} <ArrowRight className="h-4 w-4" />
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 lg:sticky lg:top-24">
              <h3 className="text-xl font-bold text-navy">Request a Free Consultation</h3>
              <p className="text-sm text-slate-600 mt-1 mb-4">We'll review qualifying systems and available protection with you.</p>
              <LeadForm defaultService="New System & Warranty" compact />
            </div>
            {spoke.relatedServices?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-navy mb-3">Related Pages</h3>
                <ul className="space-y-2">
                  {spoke.relatedServices.map((r) => (
                    <li key={r.to}>
                      <Link to={r.to} data-testid={`spoke-related-${r.to.replace(/\//g, "")}`} className="flex items-center gap-2 text-sm text-slate-700 hover:text-launch transition-colors">
                        <ArrowRight className="h-4 w-4 text-launch" /> {r.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </section>

      <div className="bg-slate-100"><FaqAccordion faqs={spoke.faqs} title="Warranty Questions, Answered" /></div>

      {/* Disclaimer */}
      <section className="py-10 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-3 rounded-lg bg-slate-50 border border-slate-200 p-4">
            <Info className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-slate-500 leading-relaxed" data-testid="spoke-disclaimer">
              Lifetime Warranty availability, coverage, eligibility, terms, conditions, limitations, and exclusions vary by qualifying equipment and program requirements. Available on qualifying systems. Details will be provided for applicable systems. This webpage is for general informational purposes and does not modify or replace the terms of any applicable warranty documentation.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
